"use client"

import Image from "next/image"
import { ShoppingCart, ExternalLink, TrendingDown, Star } from "lucide-react"
import { useFeatureFlagVariantKey } from "posthog-js/react"
import { ARTICLE_PRODUCT_PICKS, type ArticleProductPick } from "@/data/article-product-picks"
import { usePathname } from "next/navigation"
import { contentAnalytics } from "@/lib/analytics"
import { withAmazonTag } from "@/lib/affiliate"
import { PickBadgePill } from "@/components/blog/pick-badge-pill"
import { getPriceDeal, asinFromUrl } from "@/lib/price-deals"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { deriveShortName } from "@/lib/affiliate-cta"
import { upsizeAmazonImage } from "@/lib/amazon-image"
import { resolveSeasonalPicks } from "@/lib/seasonal-picks"

interface InlineProductCalloutProps {
  variant?: string
  slug?: string
}

export function InlineProductCallout({ variant = "0", slug }: InlineProductCalloutProps) {
  const pathname = usePathname()
  const effectiveSlug = slug || pathname?.split('/').pop() || ''
  // Season-aware: a pick that is out of season renders its `offSeasonAlternate`
  // instead (a spring pre-emergent showing a fall feed in September). The month
  // is read in UTC inside the resolver, so this component's SSR pass and its
  // hydration agree no matter what the viewer's timezone is. insertAfterH2 is
  // never swapped, so the marker indexes the server emitted still line up.
  const picks = ARTICLE_PRODUCT_PICKS[effectiveSlug]
    ? resolveSeasonalPicks(ARTICLE_PRODUCT_PICKS[effectiveSlug])
    : undefined
  const pickIndex = parseInt(variant, 10) || 0
  const pick = picks?.[pickIndex]

  // Analytics placement keyed off the stable badge.tier, NOT the display label — so the label
  // is free to be specific ("Best for large lawns") without silently re-bucketing clicks. Legacy
  // picks with no tier fall back to the original label mapping. Computed defensively (optional
  // chaining) so the impression hook below runs unconditionally (rules-of-hooks).
  const TIER_TO_PLACEMENT = { verdict: 'verdict-card', editor: 'editor-pick', pick: 'callout' } as const
  const placement: Parameters<typeof contentAnalytics.affiliateLinkClicked>[3] =
    pick?.badge?.tier ? TIER_TO_PLACEMENT[pick.badge.tier] :
    pick?.badge?.label === 'Verdict' ? 'verdict-card' :
    pick?.badge?.label === "Editor's Pick" ? 'editor-pick' :
    'callout'
  // Lead-vs-body position, mirroring the anchor rule in content-processor's
  // insertProductPickMarkers (the pick with the smallest insertAfterH2 is snapped to
  // the near-top lead point). Emitting it lets PostHog split blog-callout CTR by
  // position and adjudicate whether the plain-pick tier underperforms because of its
  // slot, not its treatment — before we move the curated lead anchor.
  let leadIdx = 0
  if (picks && picks.length > 0) {
    for (let i = 1; i < picks.length; i++) {
      if (picks[i].insertAfterH2 < picks[leadIdx].insertAfterH2) leadIdx = i
    }
  }
  const position = pickIndex === leadIdx ? 'lead' : 'body'

  // Experiment: "authority card" treatment for the top-tier (editor/verdict) picks.
  // blog_affiliate_authority_card_v1 buckets control/test. Fail-safe: any value
  // other than "test" — including undefined before flags resolve, or off-prod where
  // PostHog is disabled (initPostHog early-returns) — renders the control card. Only
  // the higher tiers get the treatment; plain callouts are identical in both arms.
  // Called unconditionally before the early return below (rules-of-hooks).
  const authorityVariant = useFeatureFlagVariantKey("blog_affiliate_authority_card_v1")
  // Whitelist to the two known arms so a stray flag value (or a future variant
  // added upstream) can never leak into the analytics `variant` property.
  const experimentVariant = authorityVariant === "test" ? "test" : "control"
  const showAuthority = experimentVariant === "test" && (placement === "editor-pick" || placement === "verdict-card")

  const surface = pick ? `blog-${placement}` : undefined
  const href = pick ? withAmazonTag(pick.affiliateUrl, "card") : undefined
  // Carry full product identity on the impression so shown/clicked join on
  // product_name+asin, not just surface. denominator marks this as a per-product
  // impression (vs container surfaces like comparison-table, which fire once per
  // card set) so CTR math never divides clicks by an incompatible denominator.
  const impressionRef = useAffiliateImpression(surface, {
    placement,
    slug: effectiveSlug,
    article_slug: effectiveSlug,
    position,
    variant: experimentVariant,
    product_name: pick?.name,
    affiliate_link: href,
    asin: pick ? asinFromUrl(pick.affiliateUrl) ?? undefined : undefined,
    denominator: 'product',
  })

  if (!pick) return null

  // Deal signal from committed price history (data/price-history.json). Renders nothing
  // until ~3 weekly snapshots accrue for this ASIN — see lib/price-deals.ts.
  const deal = getPriceDeal(asinFromUrl(pick.affiliateUrl), parseFloat(pick.price))

  // Specific CTA per the growth playbook: name the product instead of a generic
  // "Check price on Amazon". Prefer an authored shortName; else derive a recognizable
  // label from the first few words of the product name.
  const ctaProductLabel = pick.shortName || deriveShortName(pick.name)

  // Normalize legacy `_AC_SL200_` URLs: that variant is 86 to 127px wide on a
  // typical product shot, and this box paints it at 170px CSS (so 340 to 510
  // device pixels). See lib/amazon-image.
  const thumbnailUrl = upsizeAmazonImage(pick.imageUrl)

  const handleClick = () => {
    contentAnalytics.affiliateLinkClicked(effectiveSlug, pick.name, href ?? pick.affiliateUrl, placement)
    if (surface) {
      contentAnalytics.affiliateProductClicked(surface, {
        productName: pick.name,
        affiliateLink: href ?? pick.affiliateUrl,
        position,
        variant: experimentVariant,
        placement,
        article_slug: effectiveSlug,
      })
    }
  }

  return (
    <div
      ref={impressionRef}
      className={
        showAuthority
          ? "relative my-8 overflow-hidden rounded-[22px] border-2 border-emerald-300/70 bg-white p-5 shadow-[0_22px_56px_-22px_rgba(16,120,60,0.42)] ring-1 ring-emerald-100 sm:p-[26px]"
          : "my-8 rounded-[22px] border border-gray-200 bg-white p-5 shadow-[0_16px_44px_-22px_rgba(18,46,30,0.28)] sm:p-[26px]"
      }
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-[26px]">
        <div className="max-sm:h-[200px] w-full flex-shrink-0 overflow-hidden rounded-2xl bg-[#f3f6f3] flex items-center justify-center sm:aspect-square sm:w-[170px]">
          {thumbnailUrl ? (
            <Image
              src={thumbnailUrl}
              alt={pick.name}
              width={500}
              height={500}
              sizes="(min-width: 640px) 170px, 100vw"
              loading="lazy"
              className="h-full w-full object-contain p-3"
            />
          ) : (
            <ShoppingCart className="h-10 w-10 text-green-700/70" />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-stretch gap-3 sm:items-start">
          <div className="flex flex-wrap items-center gap-1.5">
            {pick.badge ? (
              <PickBadgePill badge={pick.badge} showStar />
            ) : (
              <PickBadgePill badge={{ label: "Our Pick", tone: "green", tier: "pick" }} showStar />
            )}
            {showAuthority && pick.badge?.rating != null && (
              <span className="inline-flex items-center gap-1 text-[13px] font-bold text-emerald-700">
                <Star className="h-4 w-4 fill-emerald-500 text-emerald-500" />
                {pick.badge.rating.toFixed(1)}
              </span>
            )}
            {deal && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-emerald-600/25 bg-emerald-600/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
                title="Based on this product's recent price history"
              >
                <TrendingDown className="h-3 w-3" />
                {deal.label}
              </span>
            )}
          </div>
          <p className="font-editorial text-[22px] font-semibold leading-[1.18] tracking-[-0.01em] text-gray-900 text-balance sm:text-2xl">
            {pick.name}
          </p>
          {showAuthority && (
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700">
              Why we picked it
            </p>
          )}
          <p className="text-[15px] leading-relaxed text-gray-700">{pick.description}</p>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow sponsored"
            data-affiliate="true"
            data-affiliate-placement="card-v1"
            onClick={handleClick}
            className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-linear-to-r from-green-600 to-emerald-600 px-[26px] min-h-[52px] text-base font-bold text-white! hover:text-white! shadow-[0_12px_26px_-10px_rgba(22,163,74,0.5)] transition hover:-translate-y-0.5 active:scale-[0.98] no-underline hover:no-underline sm:w-auto"
          >
            <span className="text-balance">See {ctaProductLabel} on Amazon</span>
            <ExternalLink className="h-[17px] w-[17px] flex-shrink-0" />
          </a>
          <p className="text-xs leading-normal text-gray-500">
            As an Amazon Associate we earn from qualifying purchases.
          </p>
        </div>
      </div>
    </div>
  )
}
